import type { EnhancedProductFamilyId, EnhancedProductVariantId } from '#api/enhancements/enhancedTmsApiTypes'
import type { BaseCheckResponse, ManCode } from '#api/tmsApiService'
import type { Brand } from '#types/Brand'

type LocalConfigurationId = Brand<`MAIC-${string}`, '__localConfigurationId'>
type EcmConfigurationVersion = Brand<number, '__EcmConfigurationVersion'>

export type CheckTaskId = Brand<string, '__taskId'>

export type OptionChangeSelectionId = `change_${string}` & { __optionChangeBrand: never }
type OptionChangeId = string & { __optionChangeBrand: never }

interface ModificationV2 {
  family: ModificationFamilyV2
}

interface ModificationFamilyV2 {
  id: EnhancedProductFamilyId
  title?: string
}

interface ModificationVariantV2 {
  id: EnhancedProductVariantId
  title: string
  price: number
}

interface RemovalV2 extends ModificationV2 {
  archetype: 'removal'
  fromVariant: ModificationVariantV2
  isImportant: boolean
}

interface AdditionV2 extends ModificationV2 {
  archetype: 'addition'
  toVariant: ModificationVariantV2
  isImportant: boolean
}

interface ChangeV2 extends ModificationV2 {
  archetype: 'change'
  fromVariant: ModificationVariantV2
  toVariant: ModificationVariantV2
  isImportant: boolean
}

interface OptionChangeConsequenceV2 {
  priceDelta: number
  priceSum: number
  additions: AdditionV2[]
  changes: ChangeV2[]
  removals: RemovalV2[]
}

interface OptionChangeQueryV2 {
  process: OptionChangeId
  started: Timestamp
  config: LocalConfigurationId
  type: 'change' | 'undo' | 'redo'
  optionSource: 'ecm' | 'national-code'
  selections: {
    family: ModificationFamilyV2
    fromVariant: ModificationVariantV2
    wasUserSelectedVariant: boolean
    toVariant: ModificationVariantV2
  }[]
}

interface OptionChangeSucceededV2 extends OptionChangeQueryV2 {
  process: OptionChangeSelectionId
  status: 'success'
  type: 'change'
  ended: Timestamp
  conflicts: ((RemovalV2 | ChangeV2) & { lastSelectionTime: Timestamp })[]
  consequences: OptionChangeConsequenceV2
}

export type OptionChangeEntryV2 = OptionChangeSucceededV2

interface PrintOptionsV2 {
  doNotPrint: boolean
  emphasize: boolean
  printDescription: boolean
  previewImage: boolean
  imageOverrides: Record<string, boolean>
}

export interface ConfigurationProductFamilyV2 {
  selectedVariant?: { id: EnhancedProductVariantId; timestamp: Timestamp }
  isLocked: boolean
  printOptions: Partial<Record<string, PrintOptionsV2>>
}

export type TaskStatusV2 = 'queued' | 'progress' | 'success' | 'failure' | 'aborted' | 'unknown'

export interface CheckResultV2<TData extends BaseCheckResponse = BaseCheckResponse> {
  id: CheckTaskId
  status: TaskStatusV2
  startTime: Timestamp
  endTime?: Timestamp
  estimatedDuration?: number
  data?: TData
  error?: unknown
}

export interface CheckResults {
  ecmVersion: EcmConfigurationVersion
  status: TaskStatusV2
  checks: CheckResultV2[]
}

export type CheckTaskResultsByVersionV2 = Partial<Record<string, CheckResults>>

export type ConfigurationOptionalEquipmentId = Brand<`optional-equipment/${ReturnType<typeof crypto.randomUUID>}`, '__optionalEquipmentId'>
export interface ConfigurationOptionalEquipmentV2 {
  id: ConfigurationOptionalEquipmentId
  title: string
  description?: string
  variants: {
    family: { id: EnhancedProductFamilyId; title: string }
    sourceVariant?: { id: EnhancedProductVariantId; title: string; price: number | undefined }
    targetVariant: { id: EnhancedProductVariantId; title: string; price: number | undefined }
    version: number
  }[]
  implicitChanges: {
    family: { id: EnhancedProductFamilyId; title: string }
    sourceVariant: { id: EnhancedProductVariantId; title: string; price: number | undefined }
    targetVariant: { id: EnhancedProductVariantId; title: string; price: number | undefined }
    version: number
  }[]
  /**
   * Determines the state of the offering, meaning:
   * - removed: has been removed, will be ignored (currently keeping it, may as well)
   * - unchecked:
   */
  status: 'removed' | 'unchecked' | 'in-progress' | 'checked' | 'offered' | 'withdrawn'
  checkVersion?: number
}

/**
 * V2 Changes:
 * - History now allows for multi-selection
 */
export interface ConfigurationStorageDataV2 {
  type: 'tms-frontend-truck-configuration'
  version: '0.2.0'
  configurationVersion?: number
  title?: string
  configuredFamilies: Partial<Record<EnhancedProductFamilyId, ConfigurationProductFamilyV2>>
  history?: OptionChangeEntryV2[]
  initialSelection?: Record<EnhancedProductFamilyId, EnhancedProductVariantId>
  checks?: CheckTaskResultsByVersionV2
  optionalEquipment?: ConfigurationOptionalEquipmentV2[]
}

export function isConfigurationStorageDataV2(value: unknown): value is ConfigurationStorageDataV2 {
  if (!value || typeof value !== 'object') {
    return false
  }

  const data = value as { type: unknown; version: unknown }
  return data.type === 'tms-frontend-truck-configuration' && data.version === '0.2.0'
}

export function readInternalStoredDataV2(manCode: ManCode) {
  const data = manCode.configuration?.MAICInternal?.keyValuePairs?.find((pair) => pair.key === 'tms-frontend-truck-configuration')?.value

  const parsedData: unknown = data && JSON.parse(data)
  if (!isConfigurationStorageDataV2(parsedData)) {
    return undefined
  }

  return parsedData
}
